import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './EngagementView.module.scss';
import SectionsCard from '../Sections/SectionsCard';
import { IconClassRoomTop, timeAgo } from '@src/constants';
import { SelectOption, User } from '@src/types';
import { 
  LessonDetails,
  useLessonsByTeacherLazyQuery,
  useLoginAnalyticsLazyQuery,
  useRegisteredUsersLazyQuery,
} from '@src/api/graphql-codegen';
import Icon from '@src/pages/dashboard/icons/Icon';
import TitleSection from '../Sections/TitleSection';
import './collapse.css'
import ReactDataGrid from '@inovua/reactdatagrid-community';
import IconSort from '../assets/table-sort.svg?react';
import ArrowRightIcon from '@src/assets/icons/chewron_right.svg?react';
import { Pagination } from 'antd';
import CompletedLessonModal from './CompletedLessonModal';
import DetailListTable from './detailListTable/DetailListTable';
import { Collapse } from 'react-collapse';
import { formatTimeFromSeconds, diffFormatTimeFromSeconds } from '@src/utils/time';
import ChevronLeft from '../assets/chevron-left.svg?react'
import ChevronRight from '../assets/chevron-right.svg?react'
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Spinner from '@src/components/spinner/Spinner';
import logger from '@src/utils/logger';

type Props = {
  isSuperAdmin?: boolean;
  selectedSchools?: SelectOption[];
  grade?: SelectOption;
  startDate?: string;
  endDate?: string;
  dateRange?: any[];
  datePickerVisible?: boolean;
  school?: SelectOption;
  sessionUser?: User;
  filterSchools: string[];
  filterGrades: string[];
  keyword?: string;
  isAussie?: boolean;
}

function EngagementView(props: Props) {
  const {
    isSuperAdmin,
    startDate = '',
    endDate = '',
    dateRange=['',''],
    datePickerVisible,
    filterSchools,
    filterGrades,
    keyword = '',
    isAussie = false,
  } = props;

  const [engagementTableSource, setEngagementTableSource] = useState<any>([]);
  const [skip, setSkip] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [completedLessons, setCompletedLessons] = useState<any[]>([]);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [userName, setUserName] = useState('')

  const [selectCard, setSelectCard] = useState(false);
  const [selectCardTitle, setSelectCardTitle] = useState('');

  const detailViewRef = useRef<HTMLDivElement>(null);
  const detailViewSecondRef = useRef<HTMLDivElement>(null);
  const [sortInfo, setSortInfo] = useState(null);
  const [sorting, setSorting] = useState(false);

  const [loadFirstMetrics, setLoadFirstMetrics] = useState(false);

  const handleSortChange = (newSortInfo) => {
    setSortInfo(newSortInfo);
    setSorting(true);
  };

  const [loadRegisteredUsers, {data: registeredUserData, loading: registeredUserDataLoading}] = useRegisteredUsersLazyQuery({ fetchPolicy: 'cache-first' })
  const [loginAnalytics, {data: loginAnalyticsData, loading: loginAnalyticsDataLoading}] = useLoginAnalyticsLazyQuery({ fetchPolicy: 'cache-first' })

  const [getEngagementData, {loading}] = useLessonsByTeacherLazyQuery({fetchPolicy: "cache-first",
    onCompleted(res) {
      if (res.lessonsByTeacher?.data) {
        setEngagementTableSource(res.lessonsByTeacher.data.map((lessonData, index) => {
          return {
            id: index,
            ...lessonData,
          }
        }))
        setTotalCount(res.lessonsByTeacher?.total ?? 0)
        setTotalTimeSpent(res.lessonsByTeacher.totalTimeSpent ?? 0);
        setLoadFirstMetrics(false);
      }
    }
  });

  useEffect(() => {
    if (filterGrades && filterSchools.length && !datePickerVisible) {
      loadRegisteredUsers({ 
        variables: {
          input: {
            fromDate: startDate,
            toDate: endDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
          }
        }
      })
      loginAnalytics({
        variables: {
          input: {
            fromDate: startDate,
            toDate: endDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
          }
        }
      })
      getEngagementData({
        variables: {
          input: {
            fromDate: startDate,
            toDate: endDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
            limit: -1,
            offset: 0
          }
        }
      });
      setLoadFirstMetrics(true);
    }
  }, [datePickerVisible, endDate, filterGrades, filterSchools, filterSchools.length, loadRegisteredUsers, loginAnalytics, startDate, getEngagementData])

  const handleModalVisibility = useCallback((data: any) => {
    setCompletedLessons(data?.items ?? [])
    setUserName(data?.teacherName ?? "")
  }, [])

  const loadData = async({ skip, sortInfo, limit }) => {
    try {
      const strStartDate = dateRange[0]
      const strEndDate = dateRange[1]

      const response = await getEngagementData({
        variables: {
          input: {
            fromDate: strStartDate,
            toDate: strEndDate,
            schoolIds: filterSchools,
            ageGroups: filterGrades,
            limit: limit,
            offset: skip,
            sortBy: sortInfo ? sortInfo.name : 'completedLessons',
            desc: sortInfo ? sortInfo.dir === 1 : true,
          }
        }
      });

      const data = response?.data?.lessonsByTeacher?.data;
      if (Array.isArray(data) && data.length > 0) {
        return data.map((lessonData, index) => {
          return {
            id: index,
            ...lessonData,
          }
        }).filter(data => data.teacherName?.toLowerCase().includes(keyword.toLowerCase()))
      } else {
        return []
      }
    } catch (error) {
      logger.error('[Engagement Data:] loading error:', error);
      return [];
    }
  }

  const dataSource = useCallback(loadData, [dateRange, filterSchools, filterGrades, keyword, getEngagementData])

  const engagementColumns = useMemo(() => {
    const _columns = [
      {
        name: 'teacherName',
        header: <div className={styles.headerCell}>Educator Name</div>,
        defaultFlex: 0.9,
        sortable: true,
        resizable: false,
        render: ({value}) => (<span className={styles.teachColumn}>{value}</span>),
        style: {
          paddingLeft: 20
        }
      },
      {
        name: 'totalLogins',
        header: <div className={styles.headerCell}>Total Logins</div>,
        defaultFlex: 0.7,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
      },
      {
        name: 'engagement',
        header: <div className={styles.headerCell}>Engagement</div>,
        defaultFlex: 0.9,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({ value }) => {
          const engagement = value ?? 0
          const graphColor = (engagement < 50) ? '#FF8181' : (engagement < 75 ? '#FFDB7F' : '#9FD2AA')
          return (
            <div className={styles.column}>
              <div className={styles.progressBackground}>
                <div className={styles.progressBar} style={{width: `${engagement}%`, backgroundColor: `${graphColor}`, paddingLeft: 4}}></div>
              </div>
            </div>
          )
        }
      },
      {
        name: 'completedLessons',
        header: <div className={styles.headerCell}>Completed Lessons</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({ value, data }) => {
          return (
            <div className={styles.activeCol} role='button' style={{cursor: 'pointer'}} onClick={() => handleModalVisibility(data)}>
              <div className={styles.inline}>
                <span>{value}</span>
                <div className={styles.iconContainer}>
                  <ArrowRightIcon className={styles.panelIcon} />
                </div>
              </div>
            </div>
          )
        }
      },
      {
        name: 'timeSpent',
        header: <div className={styles.headerCell}>Time Spent on Platform</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({value}) => {
          const timeDiff = formatTimeFromSeconds(value);
          return (
            <span className={styles.column}>{timeDiff}</span>
          )
        }
      },
      {
        name: 'lastActiveAt',
        header: <div className={styles.headerCell}>Last Active</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({value}) => {
          const timeDiff = timeAgo(value)
          return (
            <span className={styles.column}>{timeDiff}</span>
          )
        },
      }
    ]

    const _adminColumns = [
      {
        name: 'teacherName',
        header: <div className={styles.headerCell}>Educator Name</div>,
        defaultFlex: 0.9,
        sortable: true,
        resizable: false,
        render: ({value}) => (<span className={styles.teachColumn}>{value}</span>),
        style: {
          paddingLeft: 20
        }
      },
      {
        name: 'totalLogins',
        header: <div className={styles.headerCell}>Total Logins</div>,
        defaultFlex: 0.7,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
      },
      {
        name: 'completedLessons',
        header: <div className={styles.headerCell}>Completed Lessons</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({ value, data }) => {
          return (
            <div className={styles.activeCol} role='button' style={{cursor: 'pointer'}} onClick={() => handleModalVisibility(data)}>
              <div className={styles.inline}>
                <span>{value}</span>
                <div className={styles.iconContainer}>
                  <ArrowRightIcon className={styles.panelIcon} />
                </div>
              </div>
            </div>
          )
        }
      },
      {
        name: 'timeSpent',
        header: <div className={styles.headerCell}>Time Spent on Platform</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({value}) => {
          const timeDiff = formatTimeFromSeconds(value);
          return (
            <span className={styles.column}>{timeDiff}</span>
          )
        }
      },
      {
        name: 'lastActiveAt',
        header: <div className={styles.headerCell}>Last Active</div>,
        defaultFlex: 0.8,
        sortable: true,
        resizable: false,
        textAlign: 'center' as any,
        render: ({value}) => {
          const timeDiff = timeAgo(value)
          return (
            <span className={styles.column}>{timeDiff}</span>
          )
        },
      }
    ]
    return isSuperAdmin ? _columns : _adminColumns;
  }, [handleModalVisibility, isSuperAdmin])

  const detailsData = useMemo(() => {
    if (!engagementTableSource) return [];
    var details = engagementTableSource.map((detail: any, index: number) => {

      var detailValue = 0;
      if (selectCardTitle === 'Total Logins') {
        detailValue = detail?.totalLogins;
      } else if (selectCardTitle === 'Unique Logins') {
        detailValue = detail?.completedLessons;
      } else if (selectCardTitle === 'Time Spent on Platform') {
        detailValue = detail?.timeSpent;
      } else {
        detailValue = 0;
      }
      return {
        name: detail?.teacherName ?? '',
        value: detailValue,
      }
    })
    return details.sort((a: any, b: any) => {
      return (b?.value ?? 0) - (a?.value ?? 0);
    })
  }, [engagementTableSource, selectCardTitle])

  const totalDetailsData = useMemo(() => {
    if (!loginAnalyticsData?.loginAnalytics?.total.data) return [];
    var details = loginAnalyticsData?.loginAnalytics?.total?.data.map((detail: any, index: number) => {
      return {
        name: detail.name ?? '',
        value: detail.count,
      }
    })
    return details.sort((a: any, b: any) => {
      return (b?.value ?? 0) - (a?.value ?? 0);
    })
  }, [loginAnalyticsData])

  const handleLimitChange = () => {
    setSkip(skip)
  }

  const handleChangePage = useCallback((page: number) => {
    setCurrentPage(page);
    const newSkip = (page - 1) * 10;
    setSkip(newSkip);
  }, []);

  const itemRender = useCallback((_, type, originalElement) => {
    if (type === "prev") {
      return <ChevronLeft className={styles.pagePrev} />;
    }
    if (type === "next") {
      return <ChevronRight className={styles.pageNext} />;
    }
    return originalElement;
  },[]);

  const renderPaginationToolbar = useCallback(() => {
    return <div className={styles.paginationView}>
      <Pagination current={currentPage} total={totalCount} onChange={handleChangePage} className={styles.pagination} itemRender={itemRender} showSizeChanger={false} />
    </div>
  }, [currentPage, totalCount, itemRender, handleChangePage])

  const handleOpenEducator = () => {
    if (selectCard) {
      setSelectCard(false);
      setSelectCardTitle('');
      return;  
    }
    const engagementTableElement = document.getElementById('engagement')
    if (engagementTableElement) {
      engagementTableElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  const handleOpenCard = useCallback((title: string) => {
    if (title !== selectCardTitle && selectCardTitle === '') {
      setSelectCard(!!title);
      setSelectCardTitle(title);

      if ((title === 'Unique Logins' || title === 'Time Spent on Platform') && detailViewSecondRef.current) {
        detailViewSecondRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (title === 'Total Logins' && detailViewRef.current) {
        detailViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } else {
      setSelectCard(false);
      setSelectCardTitle('');
    }
  }, [detailViewSecondRef, selectCardTitle, detailViewRef])

  const isCardBlured = useCallback((cardName: string) => {
    if (selectCard && (selectCardTitle !== cardName) && selectCardTitle !== '') {
      return true
    }
    return false
  }, [selectCard, selectCardTitle])

  const isSelected = useCallback((cardName: string) => {
    if (selectCard && (selectCardTitle === cardName) && selectCardTitle !== '') {
      return true
    }
    return false
  }, [selectCard, selectCardTitle])

  const renderCustomLoad = () => {
    if (loading) {
      return (
        <div className={styles.dataGridLoading}>
          <Spinner />
        </div>
      );
    } else {
      return null;
    }
  };
  return (
    <>
      <div className={styles.sectionView}>
        <TitleSection title="Educators" leftIcon={<Icon src={IconClassRoomTop} />} />
        
        <div ref={detailViewRef} className={styles.educatorSectionView} style={{marginTop: 20}}>
        {registeredUserDataLoading ?
          <div style={{width: '48%', padding: '0'}}>
            <Skeleton height={20} highlightColor="#ffb073" />
          </div> :
          <SectionsCard
            titleLine1='Registered Educators'
            titleLine2={`${registeredUserData?.registeredUsers.total ?? 0}`}
            graphData={registeredUserData?.registeredUsers.items ?? []}
            loading={registeredUserDataLoading}
            isEducators
            startDate={startDate}
            endDate={endDate}
            width='48%'
            openCard={handleOpenEducator}
            isCardBlured={isCardBlured('Registered Educators')}
            isSelected={isSelected('Registered Educators')}
            isClickable={true}
          />}
          {loginAnalyticsDataLoading ?
          <div style={{width: '48%', padding: '0'}}>
            <Skeleton height={20} highlightColor="#ffb073" />
          </div> :
          <SectionsCard
            titleLine1='Total Logins'
            titleLine2={`${loginAnalyticsData?.loginAnalytics.total.count ?? 0}`}
            graphData={loginAnalyticsData?.loginAnalytics.total.items ?? []}
            loading={loginAnalyticsDataLoading}
            isEducators
            startDate={startDate}
            endDate={endDate}
            width='48%'
            openCard={() => handleOpenCard('Total Logins')}
            isCardBlured={isCardBlured('Total Logins')}
            isSelected={isSelected('Total Logins')}
            isClickable={true}
          />}
        </div>
        <Collapse isOpened={selectCard && selectCardTitle === 'Total Logins'}>
          <div style={{margin: "0 60px"}}>
            <DetailListTable 
              detailName={selectCardTitle}
              detailList={totalDetailsData}
              handleClose={() => handleOpenCard('')}/>
          </div>
        </Collapse> 
        <div ref={detailViewSecondRef} className={styles.educatorSectionView}>  
          {loginAnalyticsDataLoading ?
            <div style={{width: '48%', padding: '0'}}>
              <Skeleton height={20} highlightColor="#ffb073" />
            </div> :
          <SectionsCard
            titleLine1='Unique Logins'
            titleLine2={`${loginAnalyticsData?.loginAnalytics.unique.count ?? 0}`}
            graphData={loginAnalyticsData?.loginAnalytics.unique.items ?? []}
            loading={loginAnalyticsDataLoading}
            isEducators
            startDate={startDate} endDate={endDate}
            width='48%'
            openCard={() => handleOpenCard('Unique Logins')}
            isCardBlured={isCardBlured('Unique Logins')}
            isSelected={isSelected('Unique Logins')}
            isClickable={true}
          />}
          {(loadFirstMetrics && !sorting) ?
            <div style={{width: '48%', padding: '0'}}>
              <Skeleton height={20} highlightColor="#ffb073" />
            </div> :
          <SectionsCard
            titleLine1='Time Spent on Platform'
            titleLine2={diffFormatTimeFromSeconds(totalTimeSpent)}
            graphData={[]}
            loading={loadFirstMetrics && !sorting}
            isEducators
            startDate={startDate} endDate={endDate}
            width='48%'
            openCard={() => handleOpenCard('Time Spent on Platform')}
            isCardBlured={isCardBlured('Time Spent on Platform')}
            isSelected={isSelected('Time Spent on Platform')}
            isClickable={true}
          />}
        </div>
        <Collapse isOpened={selectCard && (selectCardTitle === 'Unique Logins' || selectCardTitle === 'Time Spent on Platform')}>
          <div style={{margin: "0 60px"}}>
            <DetailListTable 
              detailName={selectCardTitle}
              detailList={detailsData}
              handleClose={() => handleOpenCard('')} />
          </div>
        </Collapse>
      </div>
      <TitleSection id="engagement" title="Engagement" leftIcon={<Icon src={IconClassRoomTop} />} />
      {(loadFirstMetrics && !sorting) ?
        <div style={{padding: '0 30px'}}>
          <Skeleton height={20} highlightColor="#ffb073" />
        </div> : 
      <div className={styles.engagementView}>
        <ReactDataGrid
          idProperty="id"
          columns={engagementColumns}
          headerHeight={100}
          rowHeight={56}
          dataSource={dataSource || []}
          style={{minHeight: 734}}
          pagination
          limit={10}
          skip={skip}
          onSkipChange={setSkip}
          onLimitChange={handleLimitChange}
          enableSelection={false}
          checkboxColumn={false}
          showColumnMenuTool={false}
          loading={loading}
          renderLoadMask={renderCustomLoad}
          loadingText="Loading..."
          sortInfo={sortInfo}
          onSortInfoChange={handleSortChange}
          renderSortTool={(direction) => (
            <IconSort role='button' style={{ verticalAlign: 'middle', marginLeft: 10 }} />
          )}
          renderPaginationToolbar={renderPaginationToolbar}
        />
      </div>}
      {completedLessons.length > 0 && <CompletedLessonModal
        open={completedLessons.length > 0}
        completedLessons={[...completedLessons].sort((a: LessonDetails, b: LessonDetails) => {
          const dateA = new Date(a?.completedAt ?? "").getTime();
          const dateB = new Date(b?.completedAt ?? "").getTime();
          return dateB - dateA;
        })}
        userName={userName}
        isAussie={isAussie}
        onClose={() => handleModalVisibility(undefined)}
      />}
    </>
  )
}

export default EngagementView;
